import type { RouteLocationNormalizedLoaded } from 'vue-router'

const ORIGIN = import.meta.env.VITE_SITE_ORIGIN ?? 'https://www.takefav.com'
const STRIP = new Set(['utm_source','utm_medium','utm_campaign','utm_term','utm_content','gclid','fbclid'])

/**
 * Builds a canonical URL from a path string.
 * 
 * Rules:
 * - Always uses https://www.takefav.com as base
 * - Strips tracking parameters (utm_*, gclid, fbclid)
 * - Removes hash fragments
 * - Normalizes pathname to lowercase
 * - Removes trailing slashes (except for root)
 * - Ensures distinct, consistent URLs for SEO
 * 
 * @param urlPath - The path to normalize (e.g., '/Product/My-Product' or '/article/slug?utm_source=test')
 * @returns Full canonical URL (e.g., 'https://www.takefav.com/product/my-product')
 */
export function buildCanonical(urlPath: string) {
  const url = new URL(urlPath, ORIGIN)
  url.hash = ''
  
  // Strip tracking parameters
  for (const k of [...url.searchParams.keys()]) {
    if (STRIP.has(k)) url.searchParams.delete(k)
  }
  
  // Normalize pathname to lowercase for consistency
  url.pathname = url.pathname.toLowerCase()
  
  // Remove trailing slash (except for root)
  if (url.pathname !== '/' && url.pathname.endsWith('/')) {
    url.pathname = url.pathname.slice(0, -1)
  }
  
  return url.toString()
}

/**
 * Gets the canonical URL for a route.
 * 
 * Only returns a canonical URL if the route has opted in via meta.canonical.
 * Supports both string and function-based canonical definitions.
 * 
 * @param route - The Vue Router route object
 * @returns Canonical URL string, or undefined if route hasn't opted in
 */
export function canonicalForRoute(route: RouteLocationNormalizedLoaded) {
  // Only emit if the route opted in
  const metaCan = route.meta?.canonical
  if (!metaCan) return undefined
  
  // Support function or string
  const href = typeof metaCan === 'function' ? metaCan(route) : metaCan
  
  // Build canonical from href or fallback to normalized fullPath
  const canonical = buildCanonical(href || route.fullPath.toLowerCase())

  // Paginated listings must self-canonicalize. Pointing /category/x?page=2 at
  // /category/x tells Google page 2 is a duplicate, so it crawls it less and
  // the products only reachable from it inherit that. Google's guidance is for
  // each paginated page to be its own canonical.
  const page = Number.parseInt(String(route.query?.page ?? ''), 10)
  if (Number.isFinite(page) && page > 1) {
    const url = new URL(canonical)
    url.searchParams.set('page', String(page))
    return url.toString()
  }

  return canonical
}
